import { useDeferredValue, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye, Search, UserPlus, X } from "lucide-react";

import { studentService, type Class, type LearnerWithoutParent, type Parent, type ParentSummary } from "../../services/studentService";
import { exportAsCsv, exportAsExcel, exportAsPdf } from "../../utils/exportTable";
import { ExportButtons, type ExportFormat } from "../ExportButtons";
import { Pager } from "../Pager";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { byText, type RegisterConfig } from "../register/registerLogic";
import {
  EMPTY_PARENT_FILTERS,
  PARENT_ORDERINGS,
  PARENT_PAGE_SIZES,
  PARENT_VIEWS,
  RELATIONSHIPS,
  childrenLine,
  parentFilterCount,
  parentParams,
  portalState,
  relationshipLabel,
  type ParentFilters,
  type ParentView,
} from "./parentsLogic";

/** Every parent and guardian, paged on the server, with the views that need following up. */
export function ParentsRegister({ summary, classes, initialView, actions, onOpen }: {
  summary?: ParentSummary;
  classes: Class[];
  initialView?: ParentView;
  actions?: ReactNode;
  onOpen: (parent: Parent) => void;
}) {
  const [filters, setFilters] = useState<ParentFilters>({ ...EMPTY_PARENT_FILTERS, view: initialView ?? "all" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [exporting, setExporting] = useState(false);
  const deferred = useDeferredValue(filters);
  const params = parentParams(deferred);
  const list = useQuery({
    queryKey: ["parents", "list", params, page, pageSize],
    queryFn: () => studentService.getParentsPage({ ...params, page, page_size: pageSize }),
    keepPreviousData: true,
  });
  const update = (changes: Partial<ParentFilters>) => { setFilters((current) => ({ ...current, ...changes })); setPage(1); };
  const rows = list.data?.results ?? [];
  const total = list.data?.count ?? 0;
  const active = parentFilterCount(filters);

  const exportRows = async (format: ExportFormat) => {
    setExporting(true);
    try {
      const all = (await studentService.getParents(params)).results;
      const columns = [
        { header: "Parent / guardian", accessor: (row: Parent) => row.full_name },
        { header: "Relationship", accessor: (row: Parent) => relationshipLabel(row.relationship) },
        { header: "Phone", accessor: (row: Parent) => row.user.phone_number ?? "" },
        { header: "Emergency contact", accessor: (row: Parent) => row.emergency_contact ?? "" },
        { header: "Email", accessor: (row: Parent) => row.user.email },
        { header: "Username", accessor: (row: Parent) => row.user.username },
        { header: "Children", accessor: (row: Parent) => childrenLine(row) },
        { header: "Portal", accessor: (row: Parent) => portalState(row).label },
        { header: "Address", accessor: (row: Parent) => row.address ?? "" },
      ];
      const file = `parents-guardians-${new Date().toISOString().slice(0, 10)}`;
      if (format === "csv") exportAsCsv(`${file}.csv`, columns, all);
      if (format === "excel") exportAsExcel(`${file}.xls`, "Parents and Guardians", columns, all);
      if (format === "pdf") exportAsPdf("Parents and Guardians", columns, all);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header pb-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold text-gray-900">Parents and guardians</h3>
            <p className="mt-1 text-sm text-gray-500">Everyone with a family portal login, the children they are linked to and whether their login is in use. Open one to edit, link children, message them or reset a forgotten password.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <ExportButtons onExport={exportRows} disabled={!total} busy={exporting} what="the filtered parents" />
            {actions}
          </div>
        </div>
      </div>
      <div className="card-content space-y-4">
        <div className="filter-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div role="group" aria-label="Parents view" className="segmented">
              {PARENT_VIEWS.map((view) => (
                <button key={view.key} type="button" aria-pressed={filters.view === view.key} onClick={() => update({ view: view.key })} className="segmented-option">
                  {view.label}
                  {summary && <span className="ml-1.5 tabular-nums opacity-70">{summary[view.count]}</span>}
                </button>
              ))}
            </div>
            {active > 0 && (
              <button type="button" onClick={() => update({ search: "", relationship: "", childClass: "" })} className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline">
                <X className="h-3.5 w-3.5" /> Clear {active} {active === 1 ? "filter" : "filters"}
              </button>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="filter-field sm:col-span-2 lg:col-span-1">
              Search
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                <input type="search" value={filters.search} onChange={(event) => update({ search: event.target.value })} className="input-compact pl-8" placeholder="Name, phone, username or a child" />
              </div>
            </label>
            <label className="filter-field">
              Relationship
              <select value={filters.relationship} onChange={(event) => update({ relationship: event.target.value })} className="input-compact">
                <option value="">All</option>
                {RELATIONSHIPS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label className="filter-field">
              Child's class
              <select value={filters.childClass} onChange={(event) => update({ childClass: event.target.value })} className="input-compact">
                <option value="">All</option>
                {classes.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </label>
            <label className="filter-field">
              Sort by
              <select value={filters.ordering} onChange={(event) => update({ ordering: event.target.value as ParentFilters["ordering"] })} className="input-compact">
                {Object.entries(PARENT_ORDERINGS).map(([key, [label]]) => <option key={key} value={key}>{label}</option>)}
              </select>
            </label>
          </div>
        </div>

        {list.isLoading ? (
          <div className="space-y-2">{Array.from({ length: 6 }).map((_, index) => <div key={index} className="h-7 animate-pulse rounded bg-gray-100" />)}</div>
        ) : list.isError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            The parents could not be loaded. <button type="button" className="font-medium underline" onClick={() => list.refetch()}>Try again</button>
          </div>
        ) : !rows.length ? (
          <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">{summary?.total ? "No parents match these filters." : "No parents or guardians yet. Add the first."}</div>
        ) : (
          <div className={`space-y-3 ${list.isFetching ? "opacity-70" : ""}`}>
            <div className="sheet-wrap">
              <table className="sheet">
                <thead>
                  <tr>
                    <th className="sheet-rownum">#</th>
                    <th>Parent / guardian</th>
                    <th>Relationship</th>
                    <th>Phone</th>
                    <th>Children</th>
                    <th>Portal</th>
                    <th className="text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.id} className="cursor-pointer" onClick={() => onOpen(row)}>
                      <td className="sheet-rownum">{(page - 1) * pageSize + index + 1}</td>
                      <td><span className="font-medium text-gray-900">{row.full_name || row.user.username}</span> <span className="text-xs text-gray-500">@{row.user.username}</span></td>
                      <td>{relationshipLabel(row.relationship)}</td>
                      <td className="tabular-nums">{row.user.phone_number || <span className="text-amber-700">None</span>}</td>
                      <td className="max-w-xs">
                        {row.children_data?.length ? (
                          <span className="line-clamp-1">{row.children_data.map((child) => `${child.full_name}${child.class_name ? ` (${child.class_name})` : ""}`).join(", ")}</span>
                        ) : <span className="text-amber-700">Not linked</span>}
                      </td>
                      <td><StatusDot {...portalState(row)} /></td>
                      <td className="text-right">
                        <button type="button" onClick={(event) => { event.stopPropagation(); onOpen(row); }} className="inline-flex items-center gap-1 whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50">
                          <Eye className="h-3.5 w-3.5" /> Open
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager
              first={(page - 1) * pageSize + 1}
              shown={rows.length}
              total={total}
              noun={total === 1 ? "parent" : "parents"}
              page={page}
              pageCount={Math.max(1, Math.ceil(total / pageSize))}
              onPage={setPage}
              pageSize={pageSize}
              pageSizes={PARENT_PAGE_SIZES}
              onPageSize={(size) => { setPageSize(size); setPage(1); }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const learnerConfig: RegisterConfig<LearnerWithoutParent> = {
  search: (row) => `${row.full_name} ${row.student_id} ${row.admission_number} ${row.class_name ?? ""}`,
  views: [{ key: "all", label: "All" }],
  date: { label: "Admitted", value: (row) => row.admission_date },
  category: { label: "Class", value: (row) => row.class_name, inRowOrder: true },
  sorts: [
    { key: "class", label: "Class", compare: () => 0 },
    { key: "name", label: "Name A–Z", compare: byText((row) => row.full_name) },
    { key: "newest", label: "Newest admission", compare: (a, b) => b.admission_date.localeCompare(a.admission_date) },
  ],
};

/** Learners in school with nobody on record: link an existing parent or add one. */
export function LearnersWithoutParentPanel({ rows, isLoading, isError, onRetry, onAddParent, onLinkExisting }: {
  rows?: LearnerWithoutParent[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onAddParent: (learner: LearnerWithoutParent) => void;
  onLinkExisting: (learner: LearnerWithoutParent, parent: Parent) => void;
}) {
  const [viewing, setViewing] = useState<LearnerWithoutParent | null>(null);
  const [search, setSearch] = useState("");
  const query = useDeferredValue(search.trim());
  const found = useQuery({ queryKey: ["parents", "search", query], queryFn: () => studentService.searchParents(query), enabled: Boolean(viewing) && query.length >= 2 });
  const columns: RegisterColumn<LearnerWithoutParent>[] = [
    { header: "Learner", cell: (row) => <span className="font-medium text-gray-900">{row.full_name}</span>, exportValue: (row) => row.full_name },
    { header: "Student ID", cell: (row) => row.student_id, exportValue: (row) => row.student_id },
    { header: "Admission no.", cell: (row) => row.admission_number, exportValue: (row) => row.admission_number },
    { header: "Class", cell: (row) => row.class_name || "—", exportValue: (row) => row.class_name ?? "" },
    { header: "Admitted", cell: (row) => row.admission_date?.slice(0, 10), exportValue: (row) => row.admission_date?.slice(0, 10) },
  ];
  return (
    <>
      <ClientRegister
        title="Learners with no parent on record"
        description="Learners in school the school has nobody to call about, and whose reports and fees no family can see. Open one to link a parent already on record, or add a new one."
        rows={rows}
        config={learnerConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => { setSearch(""); setViewing(row); }}
        openLabel={() => "Find a parent"}
        noun={["learner", "learners"]}
        exportName={{ file: "learners-without-parent", title: "Learners With No Parent on Record" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="Every learner in school has a parent or guardian on record."
      />
      {viewing && (
        <DetailsModal
          title={viewing.full_name}
          headline={{ label: "No parent on record", value: viewing.class_name || "No class", note: `${viewing.student_id} · admitted ${viewing.admission_date?.slice(0, 10)}` }}
          facts={[["Student ID", viewing.student_id], ["Admission no.", viewing.admission_number]]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              <button type="button" className="btn btn-primary gap-1.5" onClick={() => { onAddParent(viewing); setViewing(null); }}><UserPlus className="h-4 w-4" /> Add a new parent</button>
            </>
          }
        >
          <section className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">A parent already on record</h4>
            <p className="text-xs text-gray-500">A brother's or sister's parent is usually already here. Search by name or phone.</p>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
              <input type="search" autoFocus value={search} onChange={(event) => setSearch(event.target.value)} className="input pl-8" placeholder="At least two letters" />
            </div>
            {query.length >= 2 && (
              <ul className="max-h-56 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 text-sm">
                {found.isLoading && <li className="px-3 py-2 text-gray-500">Searching…</li>}
                {found.data?.map((parent) => (
                  <li key={parent.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <span>
                      <span className="font-medium text-gray-900">{parent.full_name}</span>
                      <span className="ml-2 text-xs text-gray-500">{relationshipLabel(parent.relationship)} · {parent.user.phone_number || "no phone"}{parent.children_data?.length ? ` · parent of ${parent.children_data.map((child) => child.full_name).join(", ")}` : ""}</span>
                    </span>
                    <button type="button" className="btn btn-outline h-8 text-xs" onClick={() => { onLinkExisting(viewing, parent); setViewing(null); }}>Link</button>
                  </li>
                ))}
                {found.data && !found.data.length && <li className="px-3 py-2 text-gray-500">Nobody matches. Add a new parent instead.</li>}
              </ul>
            )}
          </section>
        </DetailsModal>
      )}
    </>
  );
}
